import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Icon,
  Stack,
  Textarea,
  Toggle,
} from '@eluve/components';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { type MessageKey, useTranslation } from '@eluve/i18n';

import { FeedbackLogoThumbsDown } from './FeedbackLogoThumbsDown';
import { FeedbackLogoThumbsUp } from './FeedbackLogoThumbsUp';
import { RatingSelector } from './RatingSelector';

export type Tag = {
  id: number;
  name: string;
  isTagged: boolean;
};

// Keep the canonical English tag as the stored/transmitted value (so downstream
// records stay language-independent), and map each one to its display key.
const TAG_LABEL_KEYS: Record<string, MessageKey> = {
  'Missing important details': 'appointment.feedback.tagMissingDetails',
  'Contains irrelevant details': 'appointment.feedback.tagIrrelevantDetails',
  'Contains unspoken details': 'appointment.feedback.tagUnspokenDetails',
  'Incorrect number': 'appointment.feedback.tagIncorrectNumber',
  'Incorrect body part': 'appointment.feedback.tagIncorrectBodyPart',
  'Bad formatting': 'appointment.feedback.tagBadFormatting',
  'Too verbose': 'appointment.feedback.tagTooVerbose',
};

export interface FeedbackModalProps {
  feedbackType: FeedbackTypeEnum | null;
  tags: Tag[];
  onFeedbackClick: (type: FeedbackTypeEnum) => Promise<void>;
  onTagPressedChange: (tagId: number, isTagged: boolean) => Promise<void>;
  onAdditionalFeedbackSubmit: (
    type: FeedbackTypeEnum | null,
    comment: string,
    tags: Tag[],
    rating: number | null,
  ) => Promise<void>;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  tags,
  feedbackType,
  onFeedbackClick,
  onTagPressedChange,
  onAdditionalFeedbackSubmit,
}) => {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [feedbackComment, setFeedbackComment] = useState<string>('');
  const [rating, setRating] = useState<number | null>(null);

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const toggleDialog = (open: boolean) => {
    setIsDialogOpen(open);
  };

  const handleFeedbackClick = async (type: FeedbackTypeEnum) => {
    // only allow one feedback per instance? -- this is what chat gpt does
    if (feedbackType !== null) {
      return;
    }

    setIsDialogOpen(true);

    await onFeedbackClick(type);
  };

  const handleAdditionalFeedbackSubmit = async () => {
    await onAdditionalFeedbackSubmit(
      feedbackType,
      feedbackComment,
      tags,
      rating,
    );

    setIsDialogOpen(false);
  };

  const isPositiveFeedback = feedbackType === 'POSITIVE';

  return (
    <Dialog open={isDialogOpen} onOpenChange={toggleDialog}>
      <Button
        size="sm"
        variant={feedbackType === 'POSITIVE' ? 'default' : 'outline'}
        disabled={feedbackType !== null}
        onClick={() => handleFeedbackClick('POSITIVE')}
      >
        <Icon name="ThumbsUp" size="auto" />
        {t('appointment.feedback.yes')}
      </Button>
      <Button
        size="sm"
        variant={feedbackType === 'NEGATIVE' ? 'default' : 'outline'}
        disabled={feedbackType !== null}
        onClick={() => handleFeedbackClick('NEGATIVE')}
      >
        <Icon name="ThumbsDown" size="auto" />
        {t('appointment.feedback.no')}
      </Button>

      <DialogContent size="2xl">
        <Stack
          align="center"
          justify="center"
          gap={3}
          className="sm:gap-10 sm:p-6"
        >
          {isPositiveFeedback ? (
            <FeedbackLogoThumbsUp />
          ) : (
            <FeedbackLogoThumbsDown />
          )}

          <Heading size="3xl">
            {t('appointment.feedback.additionalFeedback')}
          </Heading>

          <Stack gap={14} align="stretch">
            <Stack gap={3} align="stretch">
              <Heading size="xl" weight="semibold">
                {t('appointment.feedback.ratingPrompt')}
              </Heading>

              <RatingSelector
                onRatingSelected={(value) => setRating(value)}
                labelFor0={t('appointment.feedback.ratingPoor')}
                labelFor5={t('appointment.feedback.ratingOkay')}
                labelFor10={t('appointment.feedback.ratingExcellent')}
              />
            </Stack>
            <Stack gap={3} align="stretch">
              <Textarea
                className="h-25 w-full resize-y text-text-primary outline-hidden focus-visible:ring-0"
                value={feedbackComment}
                placeholder={t('appointment.feedback.tellMore')}
                onChange={(e) => setFeedbackComment(e.target.value)}
              />

              <div>
                {tags.map((tag) => {
                  const label = capitalizeFirstLetter(tag.name);
                  const labelKey = TAG_LABEL_KEYS[label];
                  return (
                    <Toggle
                      key={tag.id}
                      variant="outline"
                      className="my-0.5 mr-2 h-8 rounded-3xl text-xs text-text-primary hover:border-bg-accent-quaternary hover:bg-bg-accent-quaternary hover:text-text-accent-secondary data-[state=on]:border-bg-accent-quaternary data-[state=on]:bg-bg-accent-quaternary data-[state=on]:text-text-accent-secondary sm:text-sm"
                      pressed={tag.isTagged}
                      onPressedChange={(isTagged) => {
                        onTagPressedChange(tag.id, isTagged);
                      }}
                    >
                      {labelKey ? t(labelKey) : label}
                    </Toggle>
                  );
                })}
              </div>
            </Stack>
          </Stack>
        </Stack>
        <DialogFooter>
          <Button onClick={handleAdditionalFeedbackSubmit}>
            {t('appointment.feedback.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
